import type { Film } from "@/types/content";
import Container from "@/components/base/Container";
import Tag from "@/components/base/Tag";
import { formatViews } from "@/utils/format";

interface DetailHeroProps {
  film: Film;
}

export default function DetailHero({ film }: DetailHeroProps) {
  const meta = [
    { label: "导演", value: film.director },
    { label: "主演", value: film.cast.join(" / ") },
    { label: "类型", value: film.type },
    { label: "题材", value: film.genres.join(" · ") },
    { label: "地区", value: film.region },
    { label: "上映年份", value: `${film.year}` },
    { label: "片长", value: film.duration },
  ];

  return (
    <section className="relative bg-primary-900 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={film.cover}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-top opacity-25 blur-xl scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-900/90 to-primary-900/70"></div>
      </div>

      <Container className="relative">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 py-8 md:py-12">
          <div className="w-40 sm:w-48 md:w-56 shrink-0 mx-auto md:mx-0">
            <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-50/15">
              <img
                src={film.cover}
                alt={`夜勤病栋影视作品海报：${film.title}`}
                title={`${film.title} - 夜勤病栋影视作品`}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                {film.type}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-background-50/12 text-background-100 text-xs border border-background-50/20">
                {film.originalTitle}
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50">
              {film.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-400"></i>
                <strong className="text-background-50">{film.rating.toFixed(1)}</strong>
                <span className="text-background-300">/ 10</span>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line"></i>
                {formatViews(film.views)} 次浏览
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {film.year}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {film.duration}
              </span>
            </div>

            <p className="mt-4 text-sm md:text-[15px] text-background-200 leading-relaxed max-w-3xl">
              {film.summary}
            </p>

            <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-3xl">
              {meta.map((item) => (
                <div key={item.label} className="flex gap-3 text-sm">
                  <dt className="w-20 shrink-0 text-background-300">{item.label}</dt>
                  <dd className="text-background-100 min-w-0">{item.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-4 flex flex-wrap gap-2">
              {film.tags.map((tag) => (
                <Tag key={tag} label={`# ${tag}`} tone="accent" />
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}